/**
 * Single source of truth for turning a currency CODE ("PLN", "EUR", ...) into the
 * SYMBOL merchants actually want shown ("zł", "€", ...) — storefront prices were
 * showing the raw ISO code (e.g. "129.00 PLN") instead of a proper symbol.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  PLN: 'zł',
  EUR: '€',
  USD: '$',
  GBP: '£',
  CZK: 'Kč',
};

// $/£ conventionally sit before the number ("$129"); zł/€/Kč sit after it ("129 zł").
const PREFIX_SYMBOLS = new Set(['$', '£']);

export function getCurrencySymbol(currencyCode?: string | null): string {
  const code = (currencyCode || 'PLN').toUpperCase();
  return CURRENCY_SYMBOLS[code] || code;
}

/**
 * Formats a price amount with its currency symbol, dropping ",00"/".00" when the
 * amount has no cents (e.g. "129 zł" instead of "129.00 zł") while keeping real
 * cents visible (e.g. "129.99 zł") — merchants using psychological (x.99) pricing
 * never lose precision, only exact-zero cents get hidden.
 *
 * From 10 000 up the integer part is grouped in threes with a non-breaking space
 * ("1 029 000 zł" instead of "1029000 zł" — machinery/vehicle prices were unreadable),
 * matching the Polish convention of leaving 4-digit amounts ("1999 zł") ungrouped, so
 * everyday prices render exactly as before.
 */
export function formatPrice(
  amount: number | null | undefined,
  currencyCode?: string | null,
  locale?: string,
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '';
  const symbol = getCurrencySymbol(currencyCode);
  const hasCents = Math.round((amount - Math.floor(amount)) * 100) !== 0;
  let numberStr: string;
  if (locale) {
    numberStr = new Intl.NumberFormat(locale, {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  } else {
    // Compatibility path for administrative and integration surfaces which have
    // no visitor locale in scope yet. Their output stays byte-for-byte unchanged.
    numberStr = hasCents ? amount.toFixed(2) : String(Math.round(amount));
    if (Math.abs(amount) >= 10000) {
      const [intPart, decPart] = numberStr.split('.');
      numberStr = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
        + (decPart !== undefined ? `.${decPart}` : '');
    }
  }
  return PREFIX_SYMBOLS.has(symbol) ? `${symbol}${numberStr}` : `${numberStr} ${symbol}`;
}

/**
 * Masks free-text amount input down to digits + a single decimal separator (comma
 * or dot), stripping everything else — including +/- which `type="number"` inputs
 * otherwise accept and which would eat into the visible character budget.
 */
export function maskAmountInput(raw: string, maxLength = 10): string {
  let seenSeparator = false;
  let out = '';
  for (const ch of raw) {
    if (out.length >= maxLength) break;
    if (ch >= '0' && ch <= '9') {
      out += ch;
    } else if ((ch === ',' || ch === '.') && !seenSeparator && out.length > 0) {
      seenSeparator = true;
      out += ch;
    }
  }
  return out;
}
